import styled from '@emotion/styled';
import { useState, useEffect, useRef } from 'react';

// Components
import { proCatData } from './ProCatData';
import ProCatForm from './ProCatForm';
import ProCatResults from './ProCatResults';

const CalculatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CalcFormContainer = styled.div`
  background: #e6e6e6;
  padding: 15px;
  width: 100%;

  @media (min-width: 768px) {
    width: 33.333%;
  }
`;

const CalcResultsContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 66.666%;
  }
`;

const ProCatCalculator = ({ costPerBag, installers, laborRate, markup, footnotes }) => {
  const [input, setInput] = useState({
    rvalue: 'R49',
    costPerBag,
    installers,
    laborCost: laborRate,
    markup,
  });
  const [proCatInfo, setProCatInfo] = useState({});
  const [resultsVisible, setResultsVisible] = useState(false);
  const scrollToDiv = useRef();

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  const scrollHandler = () => {
    if (window.pageYOffset >= scrollToDiv.current.offsetTop) {
      setResultsVisible(true);
    } else {
      setResultsVisible(false);
    }
  };

  const inputChange = e => {
    const { name, value } = e.target;

    setInput(prevState => ({ ...prevState, [name]: value }));
  };

  const formSubmit = e => {
    e.preventDefault();

    if (input.atticSqFt && input.currentDepth) {
      const setUpTime = 1.5;
      const targetInsulationDepth = proCatData[input.rvalue].thickness;
      const insulationNeeded = targetInsulationDepth - input.currentDepth;
      const dataMatch = Object.values(proCatData)
        .filter(v => v.thickness == insulationNeeded)
        .map(match => match.numberOfBags);
      const numberOfBags = Math.round(dataMatch * (input.atticSqFt / 1000));

      const blowTime = (numberOfBags * 7) / 60;
      const materialCost = numberOfBags * input.costPerBag;
      const crewTime = blowTime + setUpTime;
      const totalCrewTime = crewTime.toFixed(1) * input.installers;
      const laborCost = totalCrewTime * input.laborCost;
      const totalCost = Number(laborCost) + Number(materialCost);
      const markupPercent = input.markup / 100;
      const markup = totalCost * markupPercent;
      const customerCost = totalCost + markup;
      const margin = customerCost - totalCost;

      setProCatInfo(prevState => ({
        ...prevState,
        targetInsulationDepth,
        insulationNeeded,
        bagsNeeded: numberOfBags,
        blowTime,
        totalMaterialCost: materialCost,
        totalLaborCost: laborCost,
        totalCost,
        customerCost,
        margin,
      }));

      setInput(prevState => ({
        ...prevState,
        sqFtError: null,
        currentDepthError: null,
      }));

      const element = scrollToDiv.current;
      const headerOffset = 110;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      {
        !resultsVisible &&
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
      }
    } else {
      setInput(prevState => ({
        ...prevState,
        sqFtError: input.atticSqFt ? null : "Please enter your attic's square ft",
        currentDepthError: input.currentDepth ? null : 'Please enter the current depth of insulation in your attic',
      }));
    }
  };

  return (
    <CalculatorContainer>
      <CalcFormContainer>
        <ProCatForm inputChange={inputChange} input={input} formSubmit={formSubmit} />
      </CalcFormContainer>

      <CalcResultsContainer ref={scrollToDiv}>
        <ProCatResults
          targetInsulationDepth={proCatInfo.targetInsulationDepth}
          insulationNeeded={proCatInfo.insulationNeeded}
          bagsNeeded={proCatInfo.bagsNeeded}
          blowTime={proCatInfo.blowTime}
          totalMaterialCost={proCatInfo.totalMaterialCost}
          totalLaborCost={proCatInfo.totalLaborCost}
          totalCost={proCatInfo.totalCost}
          customerCost={proCatInfo.customerCost}
          margin={proCatInfo.margin}
          footnotes={footnotes}
        />
      </CalcResultsContainer>
    </CalculatorContainer>
  );
};

export default ProCatCalculator;
